import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { formMaker } from "../../util";
import {
  applicationFormValidation,
  courseSelectionFormSchema,
  enums,
  stringLiterals,
  studentProfileFormSchema,
} from "../../data";
import {
  Alert,
  Button,
  ButtonGroup,
  Form,
  Modal,
  Tab,
  Tabs,
} from "react-bootstrap";
import { useSelector } from "react-redux";
import { useGetCoursesQuery } from "../../state/services/course";
import { useSubmitApplicationMutation } from "../../state/services/applications";
import { DEBUG } from "../../App";

// The confirmation message after submission is made!
function ModalConfirmation(props) {
  const { showModal, setShowModal, setSubmit } = props;

  const handleCancel = (_event) => {
    setShowModal(false);
    setSubmit(false);
  };

  const handleSubmit = (_event) => {
    setShowModal(false);
    setSubmit(true);
  };

  return (
    <Modal animation={false} show={showModal} onHide={handleCancel}>
      <Modal.Header closeButton>
        <Modal.Title>Caution</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        Once you submit this form, you will not be able to modify it. Therefore,
        please double-check to ensure all entries are correct and complete,
        including 2<sup>nd</sup> to 4<sup>th</sup> course choices if desired.
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancel}>
          Return
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// For debugging and local test!
// It adds a button to local dev to quickly populate the application form with fake data
function FakeApplicationDataForDebug(props) {
  const { setFormData } = props;
  const generateFakeData = () => {
    setFormData({
      program: "CS, BME",
      gradePointAverage: 3.1,
      classYear: enums.classYear[1].value,
      experience: enums.teachingExperience[3],
      comment: "I worked as a lead for several pilot programs.",
      creditHours: 16,
      otherJobHours: 5,
      availableHours: 14,
      enrollmentStatus: enums.enrollmentStatus[1],
      residency: enums.residency[1],
      workStudy: enums.yesNo[1],
      payroll: enums.payroll[0],
      transcript:
        "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf",
      linkedin: "https://www.linkedin.com/in/dummy-profile-29667aa1/",
      refereeName: "Ali Madooei",
      refereeEmail: "madooei@jhu.edu",
      course1: "EN.601.226 - Data Structures",
      grade1: "A",
      experience1: enums.courseExperience[2],
      head1: enums.yesNo[0],
      comment1: "I solved all the problems on leetcode.",
      course2: "EN.500.112 - Gateway Computing: JAVA",
      grade2: "A+",
      experience2: enums.courseExperience[1],
      head2: enums.yesNo[1],
    });
  };
  return (
    <div className="text-right">
      <span>Debug is enabled</span>{" "}
      <Button variant="danger" size={"sm"} onClick={generateFakeData}>
        Generate Fake Application Data
      </Button>
    </div>
  );
}

// Render a form for a student to fill out the application.
function ApplicationEditor(props) {
  // Initializing helps with validating the form when the user presses submit as soon as the form loads!
  const initialize = () => {
    const data = {};
    studentProfileFormSchema.forEach((item) => (data[item.name] = ""));
    for (let choice = 1; choice <= 4; choice++) {
      courseSelectionFormSchema(choice, []).forEach(
        (item) => (data[item.name] = "")
      );
    }
    return data;
  };

  const history = useHistory();
  const [key, setKey] = useState("choice-1");
  const [formData, setFormData] = useState(initialize());
  const [formErrors, setFormErrors] = useState({});
  const [submit, setSubmit] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [courses, setCourses] = useState([]);
  const { year, term } = props;

  const hiringSeasons = useSelector((state) => state.hiring.seasons);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const applications = useSelector((state) => state.applications.records);

  const { data } = useGetCoursesQuery({ year, term });
  const [submitApplication] = useSubmitApplicationMutation();

  useEffect(() => {
    setCourses(data);
  }, [data]);

  useEffect(() => {
    if (hiringSeasons.length === 0 || applications.length === 0) {
      // User directly accessed this route instead of getting here from dashboard.
      history.push("/dashboard");
    }
  }, [year, term, hiringSeasons, applications, history]);

  useEffect(() => {
    if (!submit) return;

    // Create the payload
    const payload = {
      status: "Submitted",
      year: year,
      term: term,
      jhed: userInfo["jhed"],
      firstName: userInfo["firstName"],
      lastName: userInfo["lastName"],
      preferredName:
        userInfo["firstName"] !== userInfo["preferredName"]
          ? userInfo["preferredName"]
          : "",
      email: userInfo["email"],
    };
    for (const key in formData) {
      if (formData.hasOwnProperty(key)) {
        if (formData[key] === 0 || formData[key]) {
          // Don't add falsy values except for 0
          payload[key] = formData[key];
        }
      }
    }

    // Post to server
    const postData = async () => {
      try {
        await submitApplication(payload);
        history.push("/dashboard");
      } catch (err) {
        DEBUG && console.log("Error:", err);
        DEBUG && console.log("Error Response:", err.error);
      }
    };

    postData();
  }, [submit, formData, history, term, userInfo, year]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Don't submit if there any of the entries are invalid
    const errors = {};
    for (const key in formData) {
      errors[key] = !applicationFormValidation(key, formData[key], formData);
    }
    setFormErrors(errors);
    if (Object.values(errors).includes(true)) {
      return;
    }

    setShowModal(true);
  };

  return (
    <div className="section container text-left">
      <ModalConfirmation
        showModal={showModal}
        setShowModal={setShowModal}
        setSubmit={setSubmit}
      />
      {DEBUG ? <FakeApplicationDataForDebug setFormData={setFormData} /> : ""}
      <Form onSubmit={handleSubmit}>
        <h3>{stringLiterals.applicationTitle}</h3>
        <p className="text-right">
          <span> * Required</span>
        </p>
        {formMaker(
          studentProfileFormSchema,
          applicationFormValidation,
          formData,
          setFormData,
          formErrors,
          setFormErrors
        )}

        <h4>{stringLiterals.courseSelectionTitle}</h4>
        <Alert variant="light" className={"text-left"}>
          You may apply for up to four courses. You should select at least one
          course. You may be considered for courses other than your choices,
          depending on the department's needs.
        </Alert>
        <Tabs id="controlled-tab" activeKey={key} onSelect={(k) => setKey(k)}>
          {["First", "Second", "Third", "Fourth"].map((choice, index) => {
            return (
              <Tab
                eventKey={`choice-${index + 1}`}
                key={index}
                title={
                  <h5>
                    {choice} Choice {choice === "First" ? <span> *</span> : ""}
                  </h5>
                }
              >
                <div>
                  <br />
                  {formMaker(
                    courseSelectionFormSchema(index + 1, courses),
                    applicationFormValidation,
                    formData,
                    setFormData,
                    formErrors,
                    setFormErrors,
                    17 + 5 * index
                  )}
                </div>
              </Tab>
            );
          })}
        </Tabs>

        <Alert
          variant={"primary"}
          style={{ marginTop: "3em", marginBottom: "1em", padding: "2em" }}
        >
          By submitting this application, I am indicating that the information I
          have provided on this application is truthful. Moreover, I authorize
          the Computer Science Department to review my semester and cumulative
          GPAs and disciplinary records from my time as a student at Johns
          Hopkins University/Peabody Institute. I understand that this
          information may be used when considering my application during the
          selection process and at any point during my tenure as a Course
          Assistant.
        </Alert>

        <div className="text-center">
          <ButtonGroup>
            <Button
              className={"section-button"}
              variant="outline-danger"
              onClick={() => history.push("/dashboard")}
            >
              Cancel
            </Button>
            <Button
              className={"section-button"}
              type="button"
              disabled={Object.values(formErrors).includes(true)}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </ButtonGroup>
        </div>
      </Form>
    </div>
  );
}

export default ApplicationEditor;
